"use client"

import * as React from "react"
import { notFound } from "next/navigation"
import Link from "next/link"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Minus, Plus, ShoppingBag, Check } from "lucide-react"
import { products } from "@/lib/merch-data"
import { CartProvider, useCart } from "@/lib/cart-context"
import { use } from "react"

interface ProductPageProps {
  params: Promise<{ slug: string }>
}

function ProductContent({ slug }: { slug: string }) {
  const product = products.find(p => p.slug === slug)
  const { addItem, itemCount } = useCart()
  const [selectedSize, setSelectedSize] = React.useState<string | undefined>(
    product?.sizes?.[1] // Default to M
  )
  const [selectedColor, setSelectedColor] = React.useState<string | undefined>(
    product?.colors?.[0]?.name
  )
  const [quantity, setQuantity] = React.useState(1)
  const [added, setAdded] = React.useState(false)

  if (!product) {
    notFound()
  }

  const handleAddToCart = () => {
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      quantity,
      size: selectedSize,
      color: selectedColor,
      image: product.images[0]
    })
    setAdded(true)
    setTimeout(() => setAdded(false), 2000)
  }

  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4)

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center justify-between mb-8">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/merch">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Merch
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link href="/merch/cart">
                <ShoppingBag className="mr-2 h-4 w-4" />
                Cart ({itemCount})
              </Link>
            </Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div className="aspect-square bg-gradient-to-br from-secondary to-muted rounded-lg flex items-center justify-center relative">
              <span className="text-8xl opacity-20">GNL</span>
              {product.featured && (
                <Badge className="absolute top-4 right-4 bg-primary">Featured</Badge>
              )}
              {!product.inStock && (
                <Badge variant="secondary" className="absolute top-4 left-4">Sold Out</Badge>
              )}
            </div>

            {/* Product Info */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">{product.category}</p>
              <h1 className="text-3xl font-bold text-foreground mb-4">{product.name}</h1>
              <p className="text-2xl font-bold text-primary mb-6">${product.price}</p>
              <p className="text-muted-foreground mb-8">{product.description}</p>

              {/* Size Selector */}
              {product.sizes && (
                <div className="mb-6">
                  <label className="text-sm font-medium text-foreground mb-2 block">Size</label>
                  <div className="flex gap-2">
                    {product.sizes.map(size => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`w-12 h-12 rounded-lg border text-sm font-medium transition-all ${
                          selectedSize === size
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:border-muted-foreground"
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Color Selector */}
              {product.colors && (
                <div className="mb-6">
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Color: {selectedColor}
                  </label>
                  <div className="flex gap-2">
                    {product.colors.map(color => (
                      <button
                        key={color.name}
                        onClick={() => setSelectedColor(color.name)}
                        className={`w-10 h-10 rounded-full border-2 transition-all ${
                          selectedColor === color.name
                            ? "border-primary ring-2 ring-primary ring-offset-2 ring-offset-background"
                            : "border-transparent"
                        }`}
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className="mb-8">
                <label className="text-sm font-medium text-foreground mb-2 block">Quantity</label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-border rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-3 hover:bg-secondary transition-colors"
                      disabled={quantity <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="px-4 text-foreground font-medium">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-3 hover:bg-secondary transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Add to Cart */}
              <Button
                size="lg"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={!product.inStock || added}
                onClick={handleAddToCart}
              >
                {added ? (
                  <>
                    <Check className="mr-2 h-5 w-5" />
                    Added to Cart
                  </>
                ) : product.inStock ? (
                  <>
                    <ShoppingBag className="mr-2 h-5 w-5" />
                    Add to Cart - ${product.price * quantity}
                  </>
                ) : (
                  "Out of Stock"
                )}
              </Button>

              {/* Shipping Info */}
              <p className="mt-4 text-sm text-muted-foreground text-center">
                Free shipping on orders over $50
              </p>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <section className="mt-16">
              <h2 className="text-xl font-bold text-foreground mb-6">You Might Also Like</h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {relatedProducts.map(p => (
                  <Link key={p.id} href={`/merch/${p.slug}`}>
                    <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                      <CardContent className="p-0">
                        <div className="aspect-square bg-gradient-to-br from-secondary to-muted flex items-center justify-center">
                          <span className="text-2xl opacity-30">GNL</span>
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-foreground">{p.name}</h3>
                          <p className="text-primary font-bold">${p.price}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default function ProductPage({ params }: ProductPageProps) {
  const { slug } = use(params)
  
  return (
    <CartProvider>
      <ProductContent slug={slug} />
    </CartProvider>
  )
}
